/**
 * LAS file detection utility for Log ASCII Standard files
 * Provides content-based detection and validation of LAS format compliance
 */
export class LASFileDetector {
  
  /**
   * Check if file content appears to be a valid LAS file
   * @param content File content as string
   * @returns True if content matches LAS format signatures
   */
  static isLASFile(content: string): boolean {
    if (!content || content.trim().length === 0) {
      return false;
    }

    try {
      // Check for basic LAS structure
      const hasVersionSection = this.hasVersionSection(content);
      const hasValidVersion = this.validateVersion(content).valid;
      const hasRequiredSections = this.validateRequiredSections(content);

      return hasVersionSection && hasValidVersion && hasRequiredSections;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if content has valid LAS version section
   * @param content File content as string
   * @returns True if ~VERSION section exists
   */
  private static hasVersionSection(content: string): boolean {
    const lines = content.split('\n');
    for (const line of lines.slice(0, 10)) { // Check first 10 lines
      const trimmed = line.trim();
      if (trimmed.startsWith('~VERSION') || trimmed.startsWith('~V')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Validate LAS version and extract version information
   * @param content File content as string
   * @returns Object with version string and validity flag
   */
  static validateVersion(content: string): { version: string; valid: boolean } {
    const lines = content.split('\n');
    let inVersionSection = false;

    for (const line of lines) {
      const trimmed = line.trim();
      
      // Start of version section
      if (trimmed.startsWith('~VERSION') || trimmed.startsWith('~V')) {
        inVersionSection = true;
        continue;
      }
      
      // End of version section (start of next section)
      if (inVersionSection && trimmed.startsWith('~') && !trimmed.startsWith('~VERSION') && !trimmed.startsWith('~V')) {
        break;
      }
      
      // Look for VERS line in version section (handle spaces around dot)
      if (inVersionSection && trimmed.match(/^\s*VERS\s*\.\s*/i)) {
        const match = trimmed.match(/VERS\s*\.\s*([0-9.]+)/i);
        if (match) {
          const version = match[1];
          const valid = version === '2.0' || version === '3.0';
          return { version, valid };
        }
      }
    }

    return { version: '', valid: false };
  }

  /**
   * Validate that all required LAS sections are present
   * @param content File content as string
   * @returns True if all required sections exist
   */
  static validateRequiredSections(content: string): boolean {
    const requiredSections = ['VERSION', 'WELL', 'CURVE', 'ASCII'];
    const foundSections = new Set<string>();
    
    const lines = content.split('\n');
    for (const line of lines) {
      const trimmed = line.trim().toLowerCase();
      
      // Check for section headers
      if (trimmed.startsWith('~')) {
        // Extract section name after tilde
        const sectionMatch = trimmed.match(/^~([a-z_]+)/);
        if (sectionMatch) {
          const sectionName = sectionMatch[1];
          
          // Map section variations to standard names
          if (sectionName === 'version' || sectionName === 'v') {
            foundSections.add('VERSION');
          } else if (sectionName === 'well' || sectionName === 'w') {
            foundSections.add('WELL');
          } else if (sectionName === 'curve' || sectionName === 'c') {
            foundSections.add('CURVE');
          } else if (sectionName === 'ascii' || sectionName === 'a' || sectionName.includes('ascii')) {
            foundSections.add('ASCII');
          }
        }
      }
    }

    // Check if all required sections were found
    return requiredSections.every(section => foundSections.has(section));
  }

  /**
   * Extract delimiter information from VERSION section
   * @param content File content as string
   * @returns Delimiter character or null if not found
   */
  static extractDelimiter(content: string): string | null {
    const lines = content.split('\n');
    let inVersionSection = false;

    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('~VERSION') || trimmed.startsWith('~V')) {
        inVersionSection = true;
        continue;
      }
      
      if (inVersionSection && trimmed.startsWith('~') && !trimmed.startsWith('~VERSION') && !trimmed.startsWith('~V')) {
        break;
      }
      
      if (inVersionSection && trimmed.match(/^\s*DLM\s*\.\s*/i)) {
        const match = trimmed.match(/DLM\s*\.\s*([A-Z]+)/i);
        if (match) {
          const dlm = match[1].toUpperCase();
          switch (dlm) {
            case 'TAB': return '\t';
            case 'COMMA': return ',';
            case 'SPACE': return ' ';
            default: return ' '; // Default to space
          }
        }
      }
    }

    return ' '; // Default delimiter
  }

  /**
   * Check if content has the basic structure of a well logging file
   * @param content File content as string
   * @returns True if structure suggests well logging data
   */
  static looksLikeWellLoggingData(content: string): boolean {
    const commonWellLoggingTerms = [
      'depth', 'time', 'gamma', 'resistivity', 'porosity', 'density',
      'caliper', 'neutron', 'well', 'drilling', 'formation', 'log',
      'hole', 'bit', 'torque', 'pressure', 'flow'
    ];

    const lowerContent = content.toLowerCase();
    let termCount = 0;

    for (const term of commonWellLoggingTerms) {
      if (lowerContent.includes(term)) {
        termCount++;
      }
    }

    // Require at least 3 well logging terms to be confident
    return termCount >= 3;
  }
}